/**
 * Where every frontend failure goes, so that none of them is invisible.
 *
 * The same rule as the backend (core/observability.py, and
 * palco_config/docs/osservabilita-errori.md):
 *
 * - a HANDLED condition — something the app recovers from on its own — is
 *   reported with `reportExpected` and counted in Grafana
 *   (`palco_frontend_events_total`), never in Sentry;
 * - an UNEXPECTED failure is reported with `reportUnexpected` and goes to Sentry.
 *
 * Handled events travel on the WebSocket as a `client_event` message. The
 * backend announces in `connection_ack` which events it counts, and only those
 * are sent: a backend that predates them announces nothing, and an unknown
 * message type would make it close the socket.
 *
 * Events raised before the socket is ready wait in a small buffer, persisted in
 * sessionStorage so that one raised right before a reload (a stale chunk) is
 * sent by the page that comes after it.
 *
 * Nothing here may throw into the caller.
 */
import * as Sentry from '@sentry/react';
import { safeSessionStorage } from 'utils/safeStorage';

/** Keep in sync with CLIENT_EVENTS in core/observability.py. */
export type ClientEvent =
  /** The page reloaded itself to pick up a deploy (a chunk of the old one was gone). */
  | 'stale_chunk_reload'
  /** The reload was not possible: the visitor saw "The platform has been updated". */
  | 'stale_chunk_fallback'
  /** A render error survived the retry: the visitor saw "Something went wrong". */
  | 'render_error_fallback'
  /** Neither the service worker nor Firebase answered within 3 s at the WebSocket handshake. */
  | 'ws_auth_timeout';

const STORAGE_KEY = 'palco.pendingClientEvents';

/** A broken page must not grow the buffer without bound. */
export const MAX_PENDING_CLIENT_EVENTS = 20;

type Sender = (event: ClientEvent) => void;

let sender: Sender | null = null;

const readPending = (): ClientEvent[] => {
  try {
    const parsed = JSON.parse(safeSessionStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writePending = (events: ClientEvent[]) => {
  if (events.length) {
    safeSessionStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } else {
    safeSessionStorage.removeItem(STORAGE_KEY);
  }
};

/**
 * Count a handled condition in Grafana. Never reaches Sentry, never throws.
 *
 * `beforeUnload`: the page is about to go away (a reload), and a message sent
 * now may never leave. The event is kept for the next page to send instead.
 */
export const reportExpected = (
  event: ClientEvent,
  { beforeUnload = false }: { beforeUnload?: boolean } = {},
): void => {
  try {
    if (sender && !beforeUnload) {
      sender(event);
      return;
    }
    const pending = readPending();
    if (pending.length < MAX_PENDING_CLIENT_EVENTS) {
      writePending([...pending, event]);
    }
  } catch {
    // Observability must never break the page.
  }
};

/** Send an unexpected failure to Sentry. Never throws. */
export const reportUnexpected = (
  error: unknown,
  context?: Record<string, unknown>,
): void => {
  try {
    Sentry.captureException(error, context ? { extra: context } : undefined);
  } catch {
    // Observability must never break the page.
  }
};

/**
 * Called by the WebSocket client at `connection_ack`, with what the backend
 * announced. Flushes the buffer: announced events are sent, the others dropped.
 */
export const attachClientEventSender = (
  send: (event: ClientEvent) => void,
  announced: unknown,
): void => {
  try {
    const supported = new Set(
      Array.isArray(announced)
        ? announced.filter((e): e is string => typeof e === 'string')
        : [],
    );
    if (!supported.size) {
      // An older backend: nothing can be sent. Keep buffering, until the cap,
      // for a backend of this page's lifetime that might announce them.
      sender = null;
      return;
    }
    sender = (event) => {
      if (supported.has(event)) send(event);
    };
    const pending = readPending();
    writePending([]);
    pending.forEach((event) => sender?.(event));
  } catch {
    // Observability must never break the page.
  }
};

/** Called when the socket goes away: events wait for the next ack. */
export const detachClientEventSender = (): void => {
  sender = null;
};
